import { forwardRef, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';

export type SLinearProgressType = 'primary' | 'error' | 'complete';

const FILL_COLOR: Record<SLinearProgressType, string> = {
  primary: 'var(--cmp-progress-active-color)',
  error: 'var(--cmp-progress-error-color)',
  complete: 'var(--cmp-progress-success-color)',
};

export interface SLinearProgressProps {
  /** 진행률 (0–100) */
  value?: number;
  /** 색상 타입 */
  type?: SLinearProgressType;
  /** 진행률 없이 무한 애니메이션 */
  indeterminate?: boolean;
  /** 하단 레이블 */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

const clamp = (v: number) => (Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0);

/** SLinearProgress — sd-linear-progress 포팅. */
export const SLinearProgress = forwardRef<HTMLDivElement, SLinearProgressProps>(
  function SLinearProgress(
    { value = 0, type = 'primary', indeterminate = false, label, className, style },
    ref,
  ) {
    const v = clamp(value);
    const fill = FILL_COLOR[type];
    const valueColor = v === 0 ? 'var(--sys-color-fg-tertiary, #888888)' : fill;
    const valueText = `${Math.round(v)}%`;

    return (
      <div
        ref={ref}
        className={cn('inline-flex w-full flex-col gap-[12px]', className)}
        style={style}
      >
        <div className="relative h-[20px] w-full overflow-hidden rounded-full bg-[var(--cmp-progress-color-track)]">
          <div
            className={cn(
              'absolute inset-y-0 left-0 h-full rounded-[inherit]',
              indeterminate
                ? 'w-full origin-left animate-linear-indeterminate'
                : 'transition-[width] duration-[400ms] ease-linear',
            )}
            style={{ background: fill, width: indeterminate ? undefined : `${v}%` }}
          />
          {!indeterminate && (
            <>
              <span
                className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 select-none text-center text-[12px] font-bold leading-[20px]"
                style={{ color: valueColor }}
              >
                {valueText}
              </span>
              <span
                className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 select-none text-center text-[12px] font-bold leading-[20px] text-[color:var(--cmp-progress-bar-text)]"
                style={{ clipPath: `inset(0 ${100 - v}% 0 0)` }}
              >
                {valueText}
              </span>
            </>
          )}
        </div>
        {label && (
          <span className="text-center text-[12px] font-normal leading-[20px] text-[color:var(--cmp-progress-color-label)]">
            {label}
          </span>
        )}
      </div>
    );
  },
);
